import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ArtboardsTransition,
  Artboard,
  Viewport,
  useIsBack,
} from 'components/sketch-frontend-common'
import ScrollbarSize from 'react-scrollbar-size'
import { floatsToCSSRGBAString } from 'utils'
import { withContentRect } from 'react-measure'
import styled from 'styled-components'
import Hotzone from './components/Hotzone'
import { useLocation } from 'react-router-dom'
import KeyHandler, { KEYUP, KEYDOWN } from 'react-key-handler'
import DocumentHead from 'components/DocumentHead'
import { customElementsObserver } from 'modules/analytics'

const flowViewBackgroundColor = '#212121'

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  background-color: ${flowViewBackgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
`

const smallest = (...values) => values.sort((a, b) => a - b)[0]

const createSrcSetFromFiles = files =>
  files
    .sort((currentFile, nextFile) => currentFile.scale - nextFile.scale)
    .reduce((srcSet, { url }, index) => `${srcSet}${url} ${index + 1}x,`, '')

const Hotzones = ({ layers, scale, hotzonesAreActive }) =>
  layers.map(layer => (
    <Hotzone
      isActive={hotzonesAreActive}
      destinationArtboardPermShortId={
        layer?.flow?.destinationArtboard?.permanentArtboardShortId ?? null
      }
      animationType={layer.flow.animationType}
      key={layer.identifier}
      isBackAction={layer.flow.isBackAction}
      width={layer.boundingRect.width}
      height={layer.boundingRect.height}
      top={layer.boundingRect.y}
      left={layer.boundingRect.x}
      scale={scale}
      keepParentScrollPosition={layer.flow.maintainScrollPosition}
    />
  ))

const FlowView = ({
  artboard,
  measureRef,
  contentRect,
  shouldShowHotzones,
}) => {
  const { prototypeHistory, isBack, backFrom } = useIsBack()

  const artboardRefs = useRef({})
  const artboardScroll = useRef({})

  const location = useLocation()
  const [hotzonesAreActive, setHotzonesAreActive] = useState(false)

  const showHotzones = () => () => {
    if (!shouldShowHotzones) return
    setHotzonesAreActive(true)
  }

  const hideHotzones = () => () => {
    if (!shouldShowHotzones) return
    setHotzonesAreActive(false)
  }

  const flashHotzones = () => {
    if (!shouldShowHotzones) return
    if (hotzonesAreActive) return
    setHotzonesAreActive(true)
    setTimeout(() => {
      setHotzonesAreActive(false)
    }, 500)
  }

  const [scrollbarDimensions, setScrollbarDimensions] = useState({
    width: 0,
    height: 0,
  })

  const updateScrollbarDimensions = () => measurements => {
    setScrollbarDimensions({
      width: measurements.scrollbarWidth,
      height: measurements.scrollbarHeight,
    })
  }

  /**
   * Scroll to scroll level or 0 when the location.key is changed
   * this will also "keepParentScrollPosition" if that state is set
   */
  useEffect(() => {
    const { key } = location
    const savedArtboardRef = artboardRefs.current[key]

    let scrollLevel = artboardScroll.current[key] || 0

    if (location.state?.keepParentScrollPosition) {
      const [previousElementScroll] = prototypeHistory.slice(-2)

      if (previousElementScroll?.key) {
        /**
         * When moving back and forward in the browser history if there's
         * already a saved scroll position in the "scrollLevels" the origin scroll position
         * should be kept instead of maintaining the parents one
         */
        scrollLevel =
          artboardScroll.current[location.key] ||
          artboardScroll.current[previousElementScroll.key]
      }
    }

    if (savedArtboardRef) {
      savedArtboardRef.scrollTo(
        0,
        backFrom?.state.keepParentScrollPosition
          ? artboardScroll.current[backFrom.key]
          : scrollLevel
      )
    }
  }, [backFrom, location, prototypeHistory])

  /**
   * Save artboard scroll level when a location.key is changed
   */
  useEffect(() => {
    const { key } = location

    const artboards = artboardRefs.current
    const scrollLevels = artboardScroll.current

    return () => {
      scrollLevels[key] = artboards[key]?.scrollTop
    }
  }, [location])

  const scaleX =
    contentRect.entry.width < artboard.viewport.width
      ? contentRect.entry.width / artboard.viewport.width
      : 1
  const scaleY =
    contentRect.entry.height < artboard.viewport.height
      ? contentRect.entry.height / artboard.viewport.height
      : 1

  const scale = smallest(scaleX, scaleY)

  const viewportWidth = Math.floor(artboard.viewport.width * scale)
  const viewportHeight = Math.floor(artboard.viewport.height * scale)

  const backgroundColor =
    !!artboard.includeBackgroundColorInExport && artboard.backgroundColor
      ? floatsToCSSRGBAString([
          artboard.backgroundColor.red,
          artboard.backgroundColor.green,
          artboard.backgroundColor.blue,
          artboard.backgroundColor.alpha,
        ])
      : '#fff'

  const flashHotzonesEventHandlers = {
    onMouseDown: flashHotzones,
    onTouchStart: flashHotzones,
  }

  const handleImageLoad = event => {
    customElementsObserver.pushEntry({
      element: event.currentTarget,
      identifier: 'prototype-image',
    })
  }

  const noScale = f => f.scale === 1
  const noScaleFiles = {
    fixed: artboard.fixedFiles.find(noScale),
    scrollable: artboard.scrollableFiles.find(noScale),
    full: artboard.fullFiles.find(noScale),
  }
  return (
    <Container ref={measureRef} {...flashHotzonesEventHandlers}>
      <DocumentHead title={artboard.name} />
      <KeyHandler
        keyEventName={KEYDOWN}
        keyValue="Shift"
        onKeyHandle={showHotzones}
      />
      <KeyHandler
        keyEventName={KEYUP}
        keyValue="Shift"
        onKeyHandle={hideHotzones}
      />
      <ScrollbarSize
        onLoad={updateScrollbarDimensions}
        onChange={updateScrollbarDimensions}
      />
      <Viewport width={viewportWidth} height={viewportHeight}>
        <ArtboardsTransition isBack={isBack}>
          <Artboard
            scrollContainerRef={ref => {
              /**
               * Save the artboard reference in the "artboardRefs"
               *
               * This is needed because when animating there might be more than aw <Artboard /> component
               * mounted. This prevents any reference from being overridden.
               */
              if (ref) {
                artboardRefs.current[location.key] = ref
              }
            }}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            contentsWidth={Math.floor(artboard.width * scale)}
            contentsHeight={Math.floor(artboard.height * scale)}
            scrollbarWidth={scrollbarDimensions.width}
            scrollbarHeight={scrollbarDimensions.height}
            backgroundColor={backgroundColor}
            fixedChildren={
              <React.Fragment>
                {artboard.fixedFiles.length !== 0 &&
                  noScaleFiles.fixed !== undefined && (
                    <img
                      src={noScaleFiles.fixed.url}
                      srcSet={createSrcSetFromFiles(artboard.fixedFiles)}
                      style={{ width: '100%' }}
                      draggable={false}
                      elementtiming="prototype-image"
                      onLoad={handleImageLoad}
                      alt=""
                    />
                  )}
                <Hotzones
                  hotzonesAreActive={hotzonesAreActive}
                  layers={artboard.layers.filter(l => l.isFixedToViewport)}
                  scale={scale}
                />
              </React.Fragment>
            }>
            <React.Fragment>
              {artboard.scrollableFiles.length !== 0 &&
                noScaleFiles.scrollable !== undefined && (
                  <img
                    src={noScaleFiles.scrollable.url}
                    srcSet={createSrcSetFromFiles(artboard.scrollableFiles)}
                    style={{ width: '100%' }}
                    draggable={false}
                    elementtiming="prototype-image"
                    onLoad={handleImageLoad}
                    alt=""
                  />
                )}
              {artboard.fullFiles.length !== 0 &&
                artboard.scrollableFiles.length === 0 &&
                noScaleFiles.full !== undefined && (
                  <img
                    src={noScaleFiles.full.url}
                    srcSet={createSrcSetFromFiles(artboard.fullFiles)}
                    style={{ width: '100%' }}
                    draggable={false}
                    elementtiming="prototype-image"
                    onLoad={handleImageLoad}
                    alt=""
                  />
                )}
              <Hotzones
                hotzonesAreActive={hotzonesAreActive}
                layers={artboard.layers.filter(l => !l.isFixedToViewport)}
                scale={scale}
              />
            </React.Fragment>
          </Artboard>
        </ArtboardsTransition>
      </Viewport>
    </Container>
  )
}

FlowView.propTypes = {
  artboard: PropTypes.object.isRequired,
  measureRef: PropTypes.func.isRequired,
  contentRect: PropTypes.object.isRequired,
  shouldShowHotzones: PropTypes.bool.isRequired,
}

export default withContentRect()(FlowView)
